// Ficha de un proyecto: sus datos y sus tareas en el orden en que conviene
// mirarlas. El alta rapida de abajo es la que mas se usa.

import { api } from "../api";
import { esc } from "../html";
import {
  iconoTilde,
  iconoChevron,
  iconoVolver,
  iconoBloqueada,
  iconoMas,
  iconoTacho,
} from "../iconos";
import {
  atributoColor,
  selectorColor,
  ETIQUETA_ESTADO_PROYECTO,
  ETIQUETA_TIPO_TAREA,
  claseEstadoProyecto,
  claseTipoTarea,
  clasePrioridad,
  claseUrgencia,
  textoUrgencia,
  etiquetaEstadoTarea,
} from "../etiquetas";
import { ESTADOS_PROYECTO, TIPOS_TAREA, PRIORIDADES } from "../../lib/tipos";
import type {
  Cliente,
  ColorProyecto,
  FichaProyecto,
  NotaBitacora,
  ProyectoListado,
  Tarea,
} from "../../lib/tipos";
import { hoyMontevideo, formatoLocal, urgencia } from "../../lib/fecha";
import { formatearCantidad } from "../../lib/calculo";

const opciones = (valores: readonly string[], etiquetas: Record<string, string>, actual: string) =>
  valores
    .map((v) => `<option value="${v}"${v === actual ? " selected" : ""}>${etiquetas[v]}</option>`)
    .join("");

function chipsTarea(t: Tarea, hoy: string): string {
  const u = urgencia(t.vence_el, hoy);
  return `
    <span class="${claseTipoTarea(t.tipo)}">${ETIQUETA_TIPO_TAREA[t.tipo]}</span>
    <span class="${clasePrioridad(t.prioridad)}">P${t.prioridad}</span>
    ${
      u && t.vence_el
        ? `<span class="${claseUrgencia(u)}">${textoUrgencia(u, formatoLocal(t.vence_el))}</span>`
        : ""
    }
    ${t.estado === "EN_CURSO" ? `<span class="chip chip-acento">En curso</span>` : ""}
    ${
      t.para === "CLIENTE"
        ? `<span class="chip chip-acento">la hace el cliente</span>`
        : ""
    }
    ${
      t.origen_presupuesto_id
        ? `<span class="chip" title="Salió de una condición firmada">del presupuesto</span>`
        : ""
    }
    ${t.horas_estimadas ? `<span class="chip">≈ ${formatearCantidad(t.horas_estimadas)} h</span>` : ""}
    ${
      t.estado_efectivo === "BLOQUEADA"
        ? `<span class="chip chip-alerta">${iconoBloqueada} esperando: ${esc(t.bloqueante_titulo)}</span>`
        : ""
    }`;
}

/** Panel que se despliega al tocar la tarea. */
function edicionTarea(t: Tarea, tareas: Tarea[]): string {
  // Se excluyen las que ya esperan a esta: el backend rechaza el ciclo igual,
  // pero no tiene sentido ofrecer una opcion que siempre va a dar error.
  const candidatos = tareas.filter(
    (o) =>
      o.id !== t.id &&
      o.bloqueada_por !== t.id &&
      o.estado !== "HECHA" &&
      o.estado !== "ANULADA",
  );
  return `
    <form class="fEditar space-y-3 border-t border-borde p-3" data-id="${t.id}">
      <label><span class="etiqueta">Título</span>
        <input name="titulo" class="campo" value="${esc(t.titulo)}" maxlength="200" required /></label>
      <div class="grid grid-cols-2 gap-3">
        <label><span class="etiqueta">Tipo</span>
          <select name="tipo" class="campo">${opciones(TIPOS_TAREA, ETIQUETA_TIPO_TAREA, t.tipo)}</select></label>
        <label><span class="etiqueta">Prioridad</span>
          <select name="prioridad" class="campo">
            ${PRIORIDADES
              .map((n) => `<option value="${n}"${n === t.prioridad ? " selected" : ""}>P${n}</option>`)
              .join("")}
          </select></label>
      </div>
      <div class="grid grid-cols-2 gap-3">
        <label><span class="etiqueta">Estado</span>
          <select name="estado" class="campo">
            <option value="PENDIENTE"${t.estado === "PENDIENTE" ? " selected" : ""}>Pendiente</option>
            <option value="EN_CURSO"${t.estado === "EN_CURSO" ? " selected" : ""}>En curso</option>
            <option value="HECHA"${t.estado === "HECHA" ? " selected" : ""}>Hecha</option>
          </select></label>
        <label><span class="etiqueta">Vence</span>
          <input name="vence_el" type="date" class="campo" value="${t.vence_el ?? ""}" /></label>
      </div>
      <label><span class="etiqueta">Horas estimadas (opcional)</span>
        <input name="horas_estimadas" type="number" step="0.5" min="0" class="campo"
               value="${t.horas_estimadas ?? ""}" placeholder="cuánto creés que te lleva" /></label>
      <label><span class="etiqueta">Esperando a</span>
        <select name="bloqueada_por" class="campo">
          <option value="">Nada, se puede hacer ya</option>
          ${candidatos
            .map(
              (o) =>
                `<option value="${o.id}"${o.id === t.bloqueada_por ? " selected" : ""}>${esc(o.titulo)}</option>`,
            )
            .join("")}
        </select></label>
      <label><span class="etiqueta">Detalle</span>
        <textarea name="detalle" class="campo" rows="3" maxlength="4000">${esc(t.detalle)}</textarea></label>
      <p class="err text-sm text-alerta"></p>
      <div class="flex gap-2">
        <button class="btn-primario flex-1">Guardar</button>
        <button type="button" class="btn-sec bAnular" data-id="${t.id}">Anular</button>
      </div>
    </form>`;
}

function filaTarea(t: Tarea, tareas: Tarea[], hoy: string): string {
  const cerrada = t.estado === "HECHA" || t.estado === "ANULADA";
  return `
    <details class="fila ${t.estado_efectivo === "BLOQUEADA" ? "opacity-70" : ""}">
      <summary class="flex items-start gap-3 p-3">
        <button type="button" class="tilde ${t.estado === "HECHA" ? "tilde-hecha" : ""}"
                data-hecha="${t.id}" data-estado="${t.estado}"
                aria-label="${t.estado === "HECHA" ? "Marcar pendiente" : "Marcar hecha"}">${iconoTilde}</button>
        <div class="min-w-0 flex-1">
          <div class="${cerrada ? "text-apagado line-through" : ""}">${esc(t.titulo)}</div>
          <div class="mt-1.5 flex flex-wrap items-center gap-1.5">${
            cerrada
              ? `<span class="chip">${etiquetaEstadoTarea(t.estado_efectivo)}${
                  t.hecha_el ? " " + formatoLocal(t.hecha_el) : ""
                }</span>`
              : chipsTarea(t, hoy)
          }</div>
        </div>
        <span class="mt-2 shrink-0 text-apagado">${iconoChevron}</span>
      </summary>
      ${edicionTarea(t, tareas)}
    </details>`;
}

/** La memoria de "en qué estaba". Va arriba de las tareas a propósito. */
function seccionBitacora(f: FichaProyecto): string {
  const notas = f.bitacora;
  return `
    <details class="tarjeta mt-5" ${notas.length ? "open" : ""}>
      <summary class="flex items-center justify-between text-sm text-apagado">
        Bitácora ${notas.length ? `(${notas.length})` : ""}
        <span>${iconoChevron}</span>
      </summary>

      <form id="fNota" class="mt-3 space-y-2">
        <textarea id="notaTexto" class="campo" rows="2" maxlength="8000"
                  placeholder="¿En qué quedaste? Escribilo como se lo contarías a vos dentro de un mes."></textarea>
        <div class="flex gap-2">
          <input id="notaEtiquetas" class="campo flex-1" maxlength="300"
                 placeholder="etiquetas separadas por coma (opcional)" />
          <button class="btn-primario px-4">Anotar</button>
        </div>
        <p id="errNota" class="text-sm text-alerta"></p>
      </form>

      ${
        notas.length > 3
          ? `<input id="buscarNota" class="campo mt-3" placeholder="Buscar en la bitácora…" />`
          : ""
      }

      <div id="listaNotas" class="mt-3 space-y-2">
        ${notas.map(nota).join("") || `<p class="text-sm text-apagado">Todavía no anotaste nada acá.</p>`}
      </div>
    </details>`;
}

function nota(n: NotaBitacora): string {
  const etiquetas = (n.etiquetas ?? "").split(",").filter(Boolean);
  return `
    <div class="nota rounded-xl border border-borde bg-realce p-3"
         data-texto="${esc((n.texto + " " + (n.etiquetas ?? "")).toLowerCase())}">
      <div class="flex items-start gap-2">
        <span class="shrink-0 font-mono text-xs text-apagado">${formatoLocal(n.fecha)}</span>
        <p class="min-w-0 flex-1 whitespace-pre-wrap break-words text-sm">${esc(n.texto)}</p>
        <button type="button" class="bSacarNota shrink-0 text-xs text-apagado hover:text-alerta"
                data-id="${n.id}" aria-label="Quitar la nota">×</button>
      </div>
      ${
        etiquetas.length
          ? `<div class="mt-1.5 flex flex-wrap gap-1">${etiquetas
              .map((e) => `<span class="chip">${esc(e)}</span>`)
              .join("")}</div>`
          : ""
      }
    </div>`;
}

/** Vínculos libres con otros proyectos: los que no salen del modelo. */
function seccionRelacionados(f: FichaProyecto, otros: ProyectoListado[]): string {
  const yaEstan = new Set(f.relacionados.map((r) => r.otro_id));
  const candidatos = otros.filter((p) => p.id !== f.proyecto.id && !yaEstan.has(p.id));

  return `
    <details class="tarjeta mt-4">
      <summary class="flex items-center justify-between text-sm text-apagado">
        Relacionado con ${f.relacionados.length ? `(${f.relacionados.length})` : ""}
        <span>${iconoChevron}</span>
      </summary>
      <div class="mt-3 flex flex-wrap gap-1.5">
        ${
          f.relacionados.length
            ? f.relacionados
                .map(
                  (r) => `
              <span class="chip flex items-center gap-1.5">
                <a href="#/proyecto/${r.otro_id}" class="hover:text-texto">${esc(r.otro_nombre)}</a>
                <button type="button" class="bSacarRel text-apagado hover:text-alerta" data-id="${r.id}"
                        aria-label="Sacar el vínculo">×</button>
              </span>`,
                )
                .join("")
            : `<span class="text-sm text-apagado">Sin vínculos con otros proyectos.</span>`
        }
      </div>
      ${
        candidatos.length
          ? `<div class="mt-3 flex gap-2">
               <select id="relNuevo" class="campo flex-1">
                 ${candidatos.map((p) => `<option value="${p.id}">${esc(p.nombre)}</option>`).join("")}
               </select>
               <button type="button" id="bRelacionar" class="btn-sec">Vincular</button>
             </div>`
          : ""
      }
    </details>`;
}

function edicionProyecto(f: FichaProyecto, clientes: Cliente[]): string {
  const p = f.proyecto;
  return `
    <details class="tarjeta mt-4">
      <summary class="flex items-center justify-between text-sm text-apagado">
        Editar proyecto <span>${iconoChevron}</span>
      </summary>
      <form id="fProyecto" class="mt-4 space-y-3">
        <label><span class="etiqueta">Nombre</span>
          <input name="nombre" class="campo" value="${esc(p.nombre)}" maxlength="120" required /></label>
        <div class="grid grid-cols-2 gap-3">
          <label><span class="etiqueta">Estado</span>
            <select name="estado" class="campo">${opciones(
              ESTADOS_PROYECTO,
              ETIQUETA_ESTADO_PROYECTO,
              p.estado,
            )}</select></label>
          <label><span class="etiqueta">Prioridad</span>
            <select name="prioridad" class="campo">
              ${PRIORIDADES
                .map((n) => `<option value="${n}"${n === p.prioridad ? " selected" : ""}>P${n}</option>`)
                .join("")}
            </select></label>
        </div>
        <div>
          <span class="etiqueta">Color</span>
          ${selectorColor(p.color)}
          <p class="mt-1.5 text-xs text-apagado">
            Para reconocerlo de un vistazo en la lista y en la pantalla Hoy. No cambia nada más.
          </p>
        </div>
        <label><span class="etiqueta">Cliente</span>
          <select name="cliente_id" class="campo">
            <option value="">Proyecto propio</option>
            ${clientes
              .map(
                (c) =>
                  `<option value="${c.id}"${c.id === p.cliente_id ? " selected" : ""}>${esc(c.nombre)}</option>`,
              )
              .join("")}
          </select></label>
        <label><span class="etiqueta">URL de producción</span>
          <input name="url_prod" class="campo" value="${esc(p.url_prod)}" maxlength="300"
                 placeholder="https://…" /></label>
        <label><span class="etiqueta">Repositorio</span>
          <input name="url_repo" class="campo" value="${esc(p.url_repo)}" maxlength="300"
                 placeholder="https://github.com/…" /></label>
        <div class="grid grid-cols-3 gap-3">
          <label><span class="etiqueta">Código</span>
            <input name="codigo" class="campo" value="${esc(p.codigo)}" maxlength="4"
                   placeholder="PP" /></label>
          <label class="col-span-2"><span class="etiqueta">Alias para dictar</span>
            <input name="alias" class="campo" value="${esc(p.alias)}" maxlength="300"
                   placeholder="pedipan, pedi pan, panadería" /></label>
        </div>
        <p class="-mt-1 text-xs text-apagado">
          El <b>código</b> es lo que decís primero al dictar. Los <b>alias</b> son las otras formas
          en que lo nombrás en voz alta, separadas por coma. Sin código, lo que dictes para este
          proyecto cae en la bandeja.
        </p>

        <label><span class="etiqueta">Stack</span>
          <textarea name="stack" class="campo" rows="3" maxlength="200">${esc(p.stack)}</textarea></label>
        <label><span class="etiqueta">Notas</span>
          <textarea name="notas" class="campo" rows="3" maxlength="4000">${esc(p.notas)}</textarea></label>
        <p id="errProyecto" class="text-sm text-alerta"></p>
        <div class="flex gap-2">
          <button class="btn-primario flex-1">Guardar</button>
          ${
            p.papelera_el
              ? `<button type="button" id="bSacarTacho" class="btn-sec">Sacar del tacho</button>`
              : p.activo === 0
                ? `<button type="button" id="bDesarchivar" class="btn-sec">Sacar del archivo</button>`
                : `<button type="button" id="bArchivar" class="btn-sec">Archivar</button>`
          }
        </div>
        ${
          p.papelera_el
            ? ""
            : `<button type="button" id="bTirarTacho" class="btn-sec w-full">
                 ${iconoTacho} Tirar al tacho
               </button>
               <p class="-mt-1 text-xs text-apagado">
                 Lo saca de todas las pantallas. Desde el tacho volvés cuando quieras, tal cual
                 estaba; recién al vaciarlo se borra. En la lista de proyectos se hace más rápido:
                 arrastrá la tarjeta hacia la izquierda.
               </p>`
        }
      </form>
    </details>`;
}

export async function renderProyecto(
  contenedor: HTMLElement,
  params: Record<string, string>,
): Promise<void> {
  const id = Number(params.id);
  if (!Number.isInteger(id) || id <= 0) {
    contenedor.innerHTML = `<div class="tarjeta text-alerta">Ese proyecto no existe.</div>`;
    return;
  }

  async function pintar(): Promise<void> {
    const [f, clientes, otrosProyectos] = await Promise.all([
      api.get<FichaProyecto>(`/api/proyectos/${id}`),
      api.get<Cliente[]>("/api/clientes"),
      api.get<ProyectoListado[]>("/api/proyectos"),
    ]);
    const hoy = hoyMontevideo();
    const p = f.proyecto;
    const abiertas = f.tareas.filter((t) => t.estado !== "HECHA" && t.estado !== "ANULADA");
    const cerradas = f.tareas.filter((t) => t.estado === "HECHA" || t.estado === "ANULADA");
    // Estimación, no medición: sale de lo que uno cargó a mano.
    const horasPendientes = abiertas.reduce((a, t) => a + (t.horas_estimadas ?? 0), 0);

    contenedor.innerHTML = `
      <div class="mb-3 flex items-center justify-between gap-2">
        <a href="#/proyectos" class="inline-flex items-center gap-1 text-sm text-apagado hover:text-texto">
          ${iconoVolver} Proyectos
        </a>
        <a href="#/grafo/${id}" class="text-sm text-apagado hover:text-texto">Ver en el grafo</a>
      </div>

      <div class="flex items-start gap-2">
        <div class="min-w-0 flex-1">
          <h1 class="titulo-pagina break-words">
            ${p.color ? `<span class="punto-color"${atributoColor(p.color)}></span>` : ""}${esc(p.nombre)}
          </h1>
          <p class="subtitulo mt-0.5">${
            p.cliente_nombre ? esc(p.cliente_nombre) : "proyecto propio"
          }${p.stack ? " · " + esc(p.stack) : ""}</p>
        </div>
        <span class="${claseEstadoProyecto(p.estado)}">${ETIQUETA_ESTADO_PROYECTO[p.estado]}</span>
      </div>

      ${
        p.url_prod || p.url_repo
          ? `<div class="mt-2 flex flex-wrap gap-3 text-sm">
               ${p.url_prod ? `<a class="text-acento hover:underline" target="_blank" rel="noreferrer" href="${esc(p.url_prod)}">producción</a>` : ""}
               ${p.url_repo ? `<a class="text-acento hover:underline" target="_blank" rel="noreferrer" href="${esc(p.url_repo)}">repositorio</a>` : ""}
             </div>`
          : ""
      }

      ${p.notas ? `<p class="mt-3 whitespace-pre-wrap text-sm text-apagado">${esc(p.notas)}</p>` : ""}

      ${seccionBitacora(f)}

      ${
        horasPendientes
          ? `<p class="mt-5 text-sm text-apagado">
               Lo que queda pendiente suma <b class="text-texto">≈ ${formatearCantidad(horasPendientes)} h</b> estimadas.
             </p>`
          : ""
      }

      <div class="mt-3 space-y-2">
        ${
          abiertas.length
            ? abiertas.map((t) => filaTarea(t, f.tareas, hoy)).join("")
            : `<div class="tarjeta text-center text-apagado">No queda nada pendiente acá.</div>`
        }
      </div>

      <form id="fRapida" class="mt-3 flex gap-2">
        <input id="tituloRapido" class="campo flex-1" placeholder="Qué falta hacer…" maxlength="200" />
        <button class="btn-primario px-3" aria-label="Agregar tarea">${iconoMas}</button>
      </form>
      <p id="errRapida" class="mt-1 text-sm text-alerta"></p>

      ${
        cerradas.length
          ? `<details class="mt-6">
               <summary class="text-sm text-apagado">Hechas y anuladas (${cerradas.length})</summary>
               <div class="mt-2 space-y-2">${cerradas
                 .map((t) => filaTarea(t, f.tareas, hoy))
                 .join("")}</div>
             </details>`
          : ""
      }

      ${edicionProyecto(f, clientes)}

      ${seccionRelacionados(f, otrosProyectos)}`;

    enganchar();
  }

  function enganchar(): void {
    // Un toque para marcar hecha, sin abrir el panel de edicion.
    // El boton se bloquea mientras viaja: si no, un doble toque la marca y la
    // desmarca, y quedan dos renglones en el registro.
    contenedor.querySelectorAll<HTMLButtonElement>("[data-hecha]").forEach((b) => {
      b.addEventListener("click", async (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        if (b.disabled) return;
        b.disabled = true;
        const estado = b.dataset.estado === "HECHA" ? "PENDIENTE" : "HECHA";
        try {
          await api.put(`/api/tareas/${b.dataset.hecha}`, { estado });
          await pintar();
        } finally {
          b.disabled = false; // si pintar() lo reemplazo, esto ya no molesta
        }
      });
    });

    contenedor.querySelectorAll<HTMLFormElement>(".fEditar").forEach((form) => {
      form.addEventListener("submit", async (ev) => {
        ev.preventDefault();
        const err = form.querySelector(".err") as HTMLElement;
        err.textContent = "";
        const d = new FormData(form);
        const bloqueada = String(d.get("bloqueada_por") ?? "");
        const vence = String(d.get("vence_el") ?? "");
        const horas = String(d.get("horas_estimadas") ?? "").trim();
        try {
          await api.put(`/api/tareas/${form.dataset.id}`, {
            horas_estimadas: horas ? Number(horas) : null,
            titulo: String(d.get("titulo") ?? "").trim(),
            tipo: d.get("tipo"),
            prioridad: Number(d.get("prioridad")),
            estado: d.get("estado"),
            vence_el: vence || null,
            bloqueada_por: bloqueada ? Number(bloqueada) : null,
            detalle: String(d.get("detalle") ?? "").trim() || null,
          });
          await pintar();
        } catch (e) {
          err.textContent = e instanceof Error ? e.message : "No se pudo guardar";
        }
      });
    });

    contenedor.querySelectorAll<HTMLButtonElement>(".bAnular").forEach((b) => {
      b.addEventListener("click", async () => {
        if (b.disabled) return;
        if (!confirm("¿Anular esta tarea? Queda en el historial, no se borra.")) return;
        b.disabled = true;
        try {
          await api.del(`/api/tareas/${b.dataset.id}`);
          await pintar();
        } finally {
          b.disabled = false;
        }
      });
    });

    // Alta rapida: titulo y Enter. Todo lo demas queda por default.
    // Mientras la tarea viaja no se manda otra: el campo se vacia y el boton
    // queda bloqueado, asi un doble toque no da de alta lo mismo dos veces.
    const rapida = contenedor.querySelector("#fRapida") as HTMLFormElement;
    let altaEnCurso = false;
    rapida.addEventListener("submit", async (ev) => {
      ev.preventDefault();
      if (altaEnCurso) return;
      const input = contenedor.querySelector("#tituloRapido") as HTMLInputElement;
      const boton = rapida.querySelector("button") as HTMLButtonElement | null;
      const err = contenedor.querySelector("#errRapida") as HTMLElement;
      err.textContent = "";
      const titulo = input.value.trim();
      if (!titulo) return;
      altaEnCurso = true;
      if (boton) boton.disabled = true;
      input.value = ""; // se vacia ya: si vuelve a tocar no reenvia lo mismo
      try {
        await api.post("/api/tareas", { proyecto_id: id, titulo });
        await pintar();
      } catch (e) {
        err.textContent = e instanceof Error ? e.message : "No se pudo agregar";
        input.value = titulo; // si fallo, se devuelve el texto para reintentar
      } finally {
        altaEnCurso = false;
        if (boton) boton.disabled = false;
      }
    });

    const fProyecto = contenedor.querySelector("#fProyecto") as HTMLFormElement;
    fProyecto.addEventListener("submit", async (ev) => {
      ev.preventDefault();
      const err = contenedor.querySelector("#errProyecto") as HTMLElement;
      err.textContent = "";
      const d = new FormData(fProyecto);
      const cliente = String(d.get("cliente_id") ?? "");
      try {
        await api.put(`/api/proyectos/${id}`, {
          codigo: String(d.get("codigo") ?? "").trim().toUpperCase() || null,
          alias: String(d.get("alias") ?? "").trim().toLowerCase() || null,
          nombre: String(d.get("nombre") ?? "").trim(),
          estado: d.get("estado"),
          prioridad: Number(d.get("prioridad")),
          // El "sin color" del selector llega como cadena vacía: va null.
          color: (String(d.get("color") ?? "") || null) as ColorProyecto | null,
          cliente_id: cliente ? Number(cliente) : null,
          url_prod: String(d.get("url_prod") ?? "").trim() || null,
          url_repo: String(d.get("url_repo") ?? "").trim() || null,
          stack: String(d.get("stack") ?? "").trim() || null,
          notas: String(d.get("notas") ?? "").trim() || null,
        });
        await pintar();
      } catch (e) {
        err.textContent = e instanceof Error ? e.message : "No se pudo guardar";
      }
    });

    const fNota = contenedor.querySelector("#fNota") as HTMLFormElement;
    fNota.addEventListener("submit", async (ev) => {
      ev.preventDefault();
      const err = contenedor.querySelector("#errNota") as HTMLElement;
      err.textContent = "";
      const texto = (contenedor.querySelector("#notaTexto") as HTMLTextAreaElement).value.trim();
      if (!texto) return;
      try {
        await api.post("/api/bitacora", {
          proyecto_id: id,
          texto,
          etiquetas:
            (contenedor.querySelector("#notaEtiquetas") as HTMLInputElement).value.trim() || null,
        });
        await pintar();
      } catch (e) {
        err.textContent = e instanceof Error ? e.message : "No se pudo anotar";
      }
    });

    // Filtro en el navegador: son como mucho 100 notas del proyecto.
    contenedor.querySelector("#buscarNota")?.addEventListener("input", (ev) => {
      const q = (ev.target as HTMLInputElement).value.trim().toLowerCase();
      contenedor.querySelectorAll<HTMLElement>(".nota").forEach((n) => {
        n.classList.toggle("hidden", !!q && !(n.dataset.texto ?? "").includes(q));
      });
    });

    contenedor.querySelectorAll<HTMLButtonElement>(".bSacarNota").forEach((b) => {
      b.addEventListener("click", async () => {
        if (!confirm("¿Quitar la nota? Deja de mostrarse, no se borra de la base.")) return;
        await api.del(`/api/bitacora/${b.dataset.id}`);
        await pintar();
      });
    });

    contenedor.querySelector("#bRelacionar")?.addEventListener("click", async () => {
      const sel = contenedor.querySelector("#relNuevo") as HTMLSelectElement;
      await api.post("/api/relaciones", {
        origen_tipo: "proyecto",
        origen_id: id,
        destino_tipo: "proyecto",
        destino_id: Number(sel.value),
      });
      await pintar();
    });

    contenedor.querySelectorAll<HTMLButtonElement>(".bSacarRel").forEach((b) => {
      b.addEventListener("click", async () => {
        await api.del(`/api/relaciones/${b.dataset.id}`);
        await pintar();
      });
    });

    contenedor.querySelector("#bArchivar")?.addEventListener("click", async () => {
      if (!confirm("¿Archivar el proyecto? Sus tareas quedan guardadas.")) return;
      await api.del(`/api/proyectos/${id}`);
      location.hash = "#/proyectos";
    });

    // El reverso: vuelve a la lista de activos como PAUSADO. Se queda en la
    // ficha —sin saltar a la lista— para poder cambiarle el estado ahi mismo.
    contenedor.querySelector("#bDesarchivar")?.addEventListener("click", async () => {
      await api.post(`/api/proyectos/${id}/restaurar`, { estado: "PAUSADO" });
      await pintar();
    });

    // Al tacho, sin preguntar: es reversible, y lo reversible no se confirma.
    // Este boton es el camino que siempre esta —en la computadora no hay dedo
    // para arrastrar—, no un duplicado del gesto de la lista.
    contenedor.querySelector("#bTirarTacho")?.addEventListener("click", async () => {
      await api.post(`/api/proyectos/${id}/papelera`, {});
      location.hash = "#/proyectos";
    });

    // Del tacho vuelve con su estado, asi que no se manda ninguno. Se queda en
    // la ficha para ver que volvio como estaba.
    contenedor.querySelector("#bSacarTacho")?.addEventListener("click", async () => {
      await api.post(`/api/proyectos/${id}/restaurar`, {});
      await pintar();
    });
  }

  try {
    await pintar();
  } catch (e) {
    contenedor.innerHTML = `<div class="tarjeta text-alerta">No se pudo cargar el proyecto: ${esc(
      e instanceof Error ? e.message : "error desconocido",
    )}</div>`;
  }
}
